import { useEffect, useRef } from 'react';
import type { Map } from 'maplibre-gl';
import useGeoData from '../../../store/useGeoData'; // Importazione default
import { extractPropertyKeys } from '../../../store/layerSlice';
import type { LayerBase, VectorSourceSpecification, GeoDataState } from '../../../store/types'; // Aggiunto GeoDataState

export const useLayerPropertyDiscovery = (mapInstance: Map | null) => {
  const dataLayers = useGeoData((state: GeoDataState) => state.dataLayers);
  const setLayerAvailablePropertyKeys = useGeoData((state: GeoDataState) => state.setLayerAvailablePropertyKeys);
  const processedLayersRef = useRef(new Set<string>()); // Tiene traccia dei layer già processati

  useEffect(() => {
    if (!mapInstance) return;

    const listeners: Array<{
      layerId: string;
      sourceId: string;
      handler: (e: any) => void;
    }> = [];

    dataLayers.forEach((layer: LayerBase) => {
      if (
        layer.sourceSpecification?.type === 'vector' &&
        (layer.sourceSpecification as VectorSourceSpecification).url && // PMTiles/MVT URL
        layer.sourceLayer &&
        (!layer.availablePropertyKeys || layer.availablePropertyKeys.length === 0) &&
        !processedLayersRef.current.has(layer.id) // Non processare se già fatto
      ) {
        const sourceId = layer.maplibreSourceId;
        if (!sourceId) return;

        const handleSourceData = (e: any) => {
          if (e.sourceId === sourceId && e.isSourceLoaded === true && e.tile) {
            // Rimuovi subito l'listener per questa sorgente specifica per evitare chiamate multiple
            mapInstance.off('sourcedata', handleSourceData);
            // Rimuovi dalla lista globale di listeners
            const listenerIndex = listeners.findIndex(l => l.sourceId === sourceId && l.handler === handleSourceData);
            if (listenerIndex > -1) {
              listeners.splice(listenerIndex, 1);
            }

            try {
              const features = mapInstance.querySourceFeatures(sourceId, {
                sourceLayer: layer.sourceLayer,
              });

              if (features && features.length > 0) {
                const featureCollection = {
                  type: 'FeatureCollection',
                  features: features,
                } as GeoJSON.FeatureCollection;
                const keys = extractPropertyKeys(featureCollection);
                setLayerAvailablePropertyKeys({ layerId: layer.id, keys });
                processedLayersRef.current.add(layer.id); // Marca come processato
                // console.log(`[useLayerPropertyDiscovery] Proprietà estratte per ${layer.name} (${layer.id}):`, keys);
              } else {
                // console.log(`[useLayerPropertyDiscovery] Nessuna feature trovata per ${layer.name} (${layer.id}) dopo il caricamento della sorgente.`);
                // Anche se non ci sono feature, consideriamo il tentativo di discovery completato per questo caricamento
                processedLayersRef.current.add(layer.id);
              }
            } catch (error) {
              console.error(
                `[useLayerPropertyDiscovery] Errore durante querySourceFeatures per ${layer.name} (${layer.id}), sourceId: ${sourceId}, sourceLayer: ${layer.sourceLayer}:`,
                error
              );
              // Marca come processato anche in caso di errore per evitare loop
              processedLayersRef.current.add(layer.id);
            }
          }
        };

        mapInstance.on('sourcedata', handleSourceData);
        listeners.push({ layerId: layer.id, sourceId, handler: handleSourceData });
        // console.log(`[useLayerPropertyDiscovery] Aggiunto listener 'sourcedata' per ${layer.name} (${layer.id}), sourceId: ${sourceId}`);
      }
    });

    return () => {
      // console.log("[useLayerPropertyDiscovery] Cleanup: rimozione listeners 'sourcedata'", listeners);
      listeners.forEach(({ handler }) => {
        if (mapInstance && mapInstance.off) { // Controlla se mapInstance e mapInstance.off esistono
            mapInstance.off('sourcedata', handler);
        }
      });
      // Non resettare processedLayersRef.current qui,
      // perché vogliamo che i layer rimangano processati per la durata del componente MapView
    };
  }, [dataLayers, mapInstance, setLayerAvailablePropertyKeys]);
};